import { Box, InputAdornment, Stack, Typography } from "@mui/material";
import {
  FieldInput,
  OutlinedSelectField,
  sanitizeDecimalMaxLen,
  sanitizeDigitsMax,
  validateCapacityFiveDigitsPositive,
  validateOptionalDecimalMaxLen,
  validateOptionalDigitsOnlyMaxLen,
} from "design-components";

export interface StorageBranchFormState {
  capacity: string;
  graceCapacity: string;
  enforcementMode: string;
  dayRate: string;
}

export const EMPTY_STORAGE_BRANCH_FORM: StorageBranchFormState = {
  capacity: "",
  graceCapacity: "",
  enforcementMode: "",
  dayRate: "",
};

/** Create prefills — day rates from operator booking settings (AV-163); empty when unavailable. */
export function buildCreateStorageForm(dayRate: string): StorageBranchFormState {
  return {
    ...EMPTY_STORAGE_BRANCH_FORM,
    dayRate,
  };
}

const CAPACITY_FOOTNOTE = "Number of spots in the yard";
const GRACE_FOOTNOTE = "Number of spots above capacity they can fill";
const ENFORCEMENT_MODE_HELPER_TEXT = "This is what will happen once the capacity is reached.";
const DAY_RATE_DECIMAL_PLACES = 2;

export interface AssociateLocationStorageSectionProps {
  title: string;
  errorPrefix: "stack" | "wheeled";
  form: StorageBranchFormState;
  errors: Record<string, string>;
  enforcementModeOptions: string[];
  enforcementLookupError: string | null;
  onFieldChange: (field: keyof StorageBranchFormState, value: string) => void;
  onFieldBlur: (field: keyof StorageBranchFormState) => void;
  onClearError: (errorKey: string) => void;
}

export function AssociateLocationStorageSection(props: AssociateLocationStorageSectionProps) {
  const {
    title,
    errorPrefix,
    form,
    errors,
    enforcementModeOptions,
    enforcementLookupError,
    onFieldChange,
    onFieldBlur,
    onClearError,
  } = props;

  const capacityKey = `${errorPrefix}Capacity`;
  const graceKey = `${errorPrefix}GraceCapacity`;
  const dayRateKey = `${errorPrefix}DayRate`;

  return (
    <Stack spacing={2.5} className="associate-location-drawer__storage-block">
      <Typography className="associate-location-drawer__section-title" component="h3">
        {title}
      </Typography>

      <Box className="associate-location-drawer__field-row">
        <FieldInput
          label="Capacity"
          required
          fullWidth
          helperText={errors[capacityKey] || CAPACITY_FOOTNOTE}
          error={Boolean(errors[capacityKey])}
          outlinedInputProps={{
            value: form.capacity,
            inputMode: "numeric",
            onChange: (e) => {
              onFieldChange("capacity", sanitizeDigitsMax(e.target.value, 5));
              onClearError(capacityKey);
            },
            onBlur: () => onFieldBlur("capacity"),
          }}
        />
        <FieldInput
          label="Grace capacity"
          fullWidth
          helperText={errors[graceKey] || GRACE_FOOTNOTE}
          error={Boolean(errors[graceKey])}
          outlinedInputProps={{
            value: form.graceCapacity,
            inputMode: "numeric",
            onChange: (e) => {
              onFieldChange("graceCapacity", sanitizeDigitsMax(e.target.value, 5));
              onClearError(graceKey);
            },
            onBlur: () => onFieldBlur("graceCapacity"),
          }}
        />
      </Box>

      <OutlinedSelectField
        label="Enforcement mode"
        fullWidth
        options={enforcementModeOptions}
        value={form.enforcementMode}
        onChange={(next) => onFieldChange("enforcementMode", next)}
        placeholder="Select enforcement mode"
        helperText={enforcementLookupError ? enforcementLookupError : ENFORCEMENT_MODE_HELPER_TEXT}
        error={Boolean(enforcementLookupError)}
      />

      <FieldInput
        label="Day rate"
        fullWidth
        error={Boolean(errors[dayRateKey])}
        helperText={errors[dayRateKey] || undefined}
        outlinedInputProps={{
          value: form.dayRate,
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
          inputMode: "decimal",
          onChange: (e) => {
            onFieldChange("dayRate", sanitizeDecimalMaxLen(e.target.value, 10, DAY_RATE_DECIMAL_PLACES));
            onClearError(dayRateKey);
          },
          onBlur: () => onFieldBlur("dayRate"),
        }}
      />
    </Stack>
  );
}

export function validateStorageBranchForm(
  prefix: "stack" | "wheeled",
  form: StorageBranchFormState,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const capErr = validateCapacityFiveDigitsPositive(form.capacity, true);
  if (capErr) errors[`${prefix}Capacity`] = capErr;
  const graceErr = validateOptionalDigitsOnlyMaxLen(form.graceCapacity, 5);
  if (graceErr) errors[`${prefix}GraceCapacity`] = graceErr;
  const dayRateErr = validateOptionalDecimalMaxLen(form.dayRate, 10, DAY_RATE_DECIMAL_PLACES);
  if (dayRateErr) errors[`${prefix}DayRate`] = dayRateErr;
  return errors;
}
